import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { Suspense, useEffect, useRef } from "react";
import { PresenceProvider } from "./contexts/PresenceContext";
import { EventSyncProvider } from "./contexts/EventSyncContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useEventSync } from "./contexts/EventSyncContext";
import { supabase } from "./lib/supabase";
import ScrollToTop from "./components/ScrollToTop";

// ─── Pages that should NOT be redirected to /evento ───────────────────────
const REDIRECT_EXEMPT_PATHS = ['/evento', '/admin', '/vendedor', '/empresa', '/entrar', '/login', '/cadastro'];

/**
 * GlobalEventRedirect — runs inside BrowserRouter
 * When event transitions to ao_vivo, redirects compradores/participantes to /evento.
 * Exempt: admin, vendor/empresa, evento itself, login/register pages.
 *
 * Uses usuarios.role to decide who is a comprador (role === 'usuario').
 * Role is fetched from usuarios table whenever auth session changes.
 */
function GlobalEventRedirect() {
  const { eventoAtivo } = useEventSync();
  const navigate = useNavigate();
  const location = useLocation();
  const prevStatusRef = useRef<string | null>(null);
  const hasRedirectedRef = useRef(false);
  const userSessionRef = useRef<any>(null);
  const userRoleRef = useRef<string | null>(null);

  // Track user session AND fetch role from usuarios table
  useEffect(() => {
    const refreshRole = async (session: any) => {
      userSessionRef.current = session;
      userRoleRef.current = null;
      if (!session?.user?.id) return;
      try {
        const { data } = await supabase
          .from('usuarios')
          .select('role, tipo_usuario')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();
        if (data) {
          const r = (data.role ?? '').toLowerCase().trim();
          const t = (data.tipo_usuario ?? '').toLowerCase().trim();
          userRoleRef.current = r || t || 'usuario';
          console.log('[GlobalEventRedirect] 👤 Role:', userRoleRef.current);
        }
      } catch {
        userRoleRef.current = null;
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      refreshRole(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      refreshRole(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const newStatus = eventoAtivo?.status ?? null;
    const prevStatus = prevStatusRef.current;

    // Detect transition TO ao_vivo
    const justWentLive = newStatus === 'ao_vivo' && prevStatus !== 'ao_vivo' && prevStatus !== null;
    prevStatusRef.current = newStatus;

    if (!justWentLive) return;

    // Reset redirect flag when new live event starts
    hasRedirectedRef.current = false;

    // Check if user is logged in
    if (!userSessionRef.current?.user) return;
    if (hasRedirectedRef.current) return;

    // Only redirect compradores/participantes (role === 'usuario' or null)
    const role = userRoleRef.current;
    if (role && role !== 'usuario') {
      console.log('[GlobalEventRedirect] ⏭️ Não redirecionando — role:', role);
      return;
    }

    // Don't redirect if already on an exempt page
    const currentPath = location.pathname;
    const isExempt = REDIRECT_EXEMPT_PATHS.some(p => currentPath.startsWith(p));
    if (isExempt) return;

    hasRedirectedRef.current = true;
    console.log('[GlobalEventRedirect] 🚀 Evento ao_vivo — redirecionando comprador para /evento');
    navigate('/evento');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoAtivo?.status]);

  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <img
          src="https://static.readdy.ai/image/4a9ef0680c9361573f145f33d8bd3992/2981b746e28fb0a5b9cec93a656624fd.png"
          alt="30go"
          style={{ width: '120px', height: 'auto', filter: 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.5))' }}
        />
        <div className="flex items-center gap-3 text-white text-sm">
          <i className="ri-loader-4-line animate-spin text-[#FF6B35] text-2xl"></i>
          <span className="text-gray-400">Carregando...</span>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter basename={__BASE_PATH__}>
          <PresenceProvider>
            {/* EventSyncProvider runs at root — ALL pages share one realtime channel */}
            <EventSyncProvider>
              <Suspense fallback={<PageLoader />}>
                <ScrollToTop />
                <GlobalEventRedirect />
                <AppRoutes />
              </Suspense>
            </EventSyncProvider>
          </PresenceProvider>
        </BrowserRouter>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
